import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import { Dialog, DialogProps, Divider } from '@mui/material';
import { Box } from '@mui/system';
import AccountSetupListItem from 'app/shared-components/AccountSetupListItem';
import VerticalDottedDivider from 'app/shared-components/VerticalDottedDivider';
import { useState } from 'react';
import { getStateCode } from 'src/app/consts/statesList';
import { IKurvPayload, IOfferAcceptPayload, IOffersItem } from 'src/app/types';
import { kurvFormConfig } from './formConfig';
import FuseLoading from '@fuse/core/FuseLoading';
import { KurvFormSlice, kurvFormSlice } from 'app/store/kurvFormSlice';
import { ModalComponent } from 'app/shared-components/modals/modalManager';

function mapStoreToPayload(store: KurvFormSlice): IKurvPayload {
  const eComEquipment: string[] = [];

  if (store.equipment?.retailKurvMobileB200)
    eComEquipment.push('Kurv Mobile (B200 reader w/chip reader)');
  if (store.equipment?.retailKurvMobileB250)
    eComEquipment.push('Kurv Mobile (B250 reader w/chip and tap)');
  if (store.equipment?.retailDejavooZ8) eComEquipment.push('Dejavoo Z8');
  if (store.equipment?.retailDejavooZ9) eComEquipment.push('Dejavoo Z9 (wireless)');
  if (store.equipment?.retailIHaveMyOwn) eComEquipment.push('I have my own');

  const retailEquipment: string[] = [];
  if (store.equipment?.eComConvenuePay) retailEquipment.push('ConvenuePay');
  if (store.equipment?.eComAuthNet) retailEquipment.push('Auth.net');
  if (store.equipment?.eComIHaveMyOwn) retailEquipment.push('I have my own');

  return {
    bank: {
      dda: store.accountProcessing!.dda,
      routing: '123456789',
    },
    data: {
      boardingBankId: 1, // Harris
      processing: store.basicInformation!.processing,
    },
    merchant: {
      ein: store.accountProcessing!.ein,
    },
    owner: {
      idNum: store.accountProcessing!.idNum,
      ssn: store.accountProcessing!.ssn,
      state: getStateCode(store.basicInformation!.state),
      idState: getStateCode(store.basicInformation!.state),
      ownership: store.basicInformation!.ownership,
      city: store.basicInformation!.city,
      county: store.basicInformation!.county,
      zip: store.basicInformation!.zip,
    },
    processing: {
      ccInvestigations: store.businessCompliance!.ccInvestigations,
      ccPciCompliant: store.businessCompliance!.ccPciCompliant,
      ccTermination: store.businessCompliance!.ccTermination,
      ccThirdParty: store.businessCompliance!.ccThirdParty,
      internet: store.salesAndTransactions!.internet.toString(),
      keyed: store.salesAndTransactions!.keyed.toString(),
      mailOrder: store.salesAndTransactions!.mailOrder.toString(),
      offSite: store.salesAndTransactions!.offSite.toString(),
      onSite: store.salesAndTransactions!.onSite.toString(),
      swiped: store.salesAndTransactions!.swiped.toString(),
      telephoneOrder: store.salesAndTransactions!.telephoneOrder.toString(),
      salesVol: store.accountProcessing!.sales_vol,
      ccVisaRiskPrograms: store.businessCompliance?.ccVisaRiskPrograms,
    },
    custom: { eComEquipment, retailEquipment },
  };
}

export const KurvFormModal: ModalComponent<
  { offer: IOffersItem },
  IOfferAcceptPayload | undefined
> = ({ open, props, onClose }) => {
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [currentForm, setCurrentForm] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const handleDialogClose: DialogProps['onClose'] = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    onClose(undefined);
  };

  const handlePrevStep = () => {
    if (currentForm === 0) onClose(undefined);
    else setCurrentForm(currentForm - 1);
  };

  const handleNextStep = () => {
    if (currentForm + 1 < kurvFormConfig.length) {
      setCurrentForm((prev) => prev + 1);
      return;
    }
    try {
      setLoading(true);
      onClose({ kurvPayload: mapStoreToPayload(kurvFormSlice.getState()) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
        },
      }}
    >
      {loading && (
        <div className="fixed inset-0 flex bg-[#0000008a] z-50 [&>div>p]:text-black">
          <FuseLoading />
        </div>
      )}

      <Box className="flex flex-row rounded-0 overflow-hidden h-[700px] w-full md:shadow-1 md:drop-shadow-md md:rounded-2xl md:max-w-7xl">
        <Box className="relative hidden md:flex flex-col justify-start h-auto overflow-hidden text-white w-1/3 bg-blue-dark pt-[24px] pl-[30px]">
          <div className="font-600 text-20 color-white">Kurv</div>
          <Divider className="ml-[-30px] border-[#FFFFFF3B] mt-[20px]" />
          <div className="z-10 mt-32">
            <div className="tracking-tight mt-28">
              {kurvFormConfig.map((config, index) => (
                <div key={index}>
                  <AccountSetupListItem
                    number={`${index + 1}`}
                    title={config.title}
                    text={config.text}
                    isSelected={index === currentForm}
                    isCompleted={index < currentForm}
                  />
                  {index < kurvFormConfig.length - 1 && <VerticalDottedDivider />}
                </div>
              ))}
            </div>
          </div>
        </Box>
        <Box className="flex flex-col h-full overflow-hidden w-full md:w-2/3 pt-[24px] px-[30px] pb-[24px] bg-white">
          <div className="flex justify-between">
            <div className="font-600 text-20 color-[#111827]">Require additional information</div>
            <button type="button" onClick={() => onClose(undefined)} className="self-end	">
              <FuseSvgIcon>material-outline:clear</FuseSvgIcon>
            </button>
          </div>
          <Divider className="ml-[-30px] border-[#1118273B] mt-[20px]" />
          {kurvFormConfig[currentForm].form(handleNextStep, handlePrevStep, props.offer)}
        </Box>
      </Box>
    </Dialog>
  );
};
